import * as React from "react"
import { Icon } from "../Icon/Icon"
import { IconButton } from "../IconButton/IconButton"
import { Button } from "../ui/button"
import { Pagination as PaginationBase, PaginationContent, PaginationItem } from "../ui/pagination"
import { ELLIPSIS_INDICATOR, usePagination } from "./use-pagination"

export interface PaginationProps {
  /**
   * The total number of items across all pages.
   */
  totalCount: number

  /**
   * The number of items to display per page.
   */
  pageSize: number

  /**
   * The currently active page.
   */
  currentPage: number

  /**
   * Callback function invoked when the page is changed.
   * It receives the new page number as an argument.
   */
  onPageChange: (page: number) => void

  /**
   * The number of page links to display on each side of the current page.
   * @default 2
   */
  siblingCount?: number

  /**
   * Optional className to be applied to the root nav element.
   */
  className?: string
}

/**
 * Validates pagination input parameters and logs warnings in development
 * @param totalCount - Total number of items
 * @param pageSize - Items per page
 * @param currentPage - Current active page
 * @param siblingCount - Sibling pages count
 * @returns true if all inputs are valid, false otherwise
 */
const validatePaginationInputs = (
  totalCount: number,
  pageSize: number,
  currentPage: number,
  siblingCount: number
): boolean => {
  const isDevelopment = process.env.NODE_ENV !== "production"

  if (typeof totalCount !== "number" || Number.isNaN(totalCount) || totalCount < 0) {
    if (isDevelopment) {
      console.warn("Pagination: totalCount must be a non-negative number", {
        totalCount,
      })
    }
    return false
  }

  if (
    typeof pageSize !== "number" ||
    Number.isNaN(pageSize) ||
    pageSize <= 0 ||
    !Number.isInteger(pageSize)
  ) {
    if (isDevelopment) {
      console.warn("Pagination: pageSize must be a positive integer", {
        pageSize,
      })
    }
    return false
  }

  if (
    typeof siblingCount !== "number" ||
    Number.isNaN(siblingCount) ||
    siblingCount < 0 ||
    !Number.isInteger(siblingCount)
  ) {
    if (isDevelopment) {
      console.warn("Pagination: siblingCount must be a non-negative integer", {
        siblingCount,
      })
    }
    return false
  }

  if (
    typeof currentPage !== "number" ||
    Number.isNaN(currentPage) ||
    currentPage < 1 ||
    !Number.isInteger(currentPage)
  ) {
    if (isDevelopment) {
      console.warn("Pagination: currentPage must be a positive integer >= 1", {
        currentPage,
      })
    }
    return false
  }

  return true
}

/**
 * Clamps currentPage to valid range and warns if adjustment was needed
 * @param currentPage - The current page value
 * @param totalPages - Total number of pages
 * @returns Clamped currentPage value
 */
const getValidCurrentPage = (currentPage: number, totalPages: number): number => {
  const isDevelopment = process.env.NODE_ENV !== "production"

  if (totalPages === 0) {
    return 1
  }

  if (currentPage > totalPages) {
    if (isDevelopment) {
      console.warn(
        "Pagination: currentPage exceeds total pages. Component will clamp to last page. " +
          "Consider updating currentPage in parent component.",
        {
          currentPage,
          totalPages,
          clampedTo: totalPages,
        }
      )
    }
    return totalPages
  }

  return currentPage
}

/**
 * A controlled, responsive Pagination component that allows users to navigate through multiple pages of data.
 * Automatically adapts to different screen sizes and provides intuitive navigation controls.
 */
export const Pagination = React.memo<PaginationProps>(
  ({ totalCount, pageSize, currentPage, onPageChange, siblingCount = 2, className }) => {
    const totalPages = Math.ceil(totalCount / pageSize)

    // Get a valid currentPage (clamps if needed and warns in development)
    const validCurrentPage = getValidCurrentPage(currentPage, totalPages)

    const visiblePageNumbers = usePagination({
      totalCount,
      pageSize,
      siblingCount,
      currentPage: validCurrentPage,
    })

    // Validate critical inputs first - return null for truly invalid states
    if (!validatePaginationInputs(totalCount, pageSize, currentPage, siblingCount)) {
      return null
    }

    // Don't render if there's no data or only one page
    if (totalCount === 0 || totalPages <= 1) {
      return null
    }

    const isFirstPage = validCurrentPage === 1
    const isLastPage = validCurrentPage === totalPages

    const handlePrevious = () => {
      if (!isFirstPage) {
        onPageChange(validCurrentPage - 1)
      }
    }

    const handleNext = () => {
      if (!isLastPage) {
        onPageChange(validCurrentPage + 1)
      }
    }

    const handlePageClick = (pageNumber: number | string) => {
      if (typeof pageNumber === "number") {
        onPageChange(pageNumber)
      }
    }

    const renderPageNumbers = () => {
      return visiblePageNumbers.map((pageNumber) => {
        if (pageNumber === ELLIPSIS_INDICATOR) {
          return (
            <span
              key={pageNumber}
              className="inline-flex items-center justify-center w-8 h-8 p-2 rounded-100 text-14 font-500 min-w-8 text-action-base-text"
              aria-label="More pages"
              role="presentation"
            >
              ...
            </span>
          )
        }

        const isActive = pageNumber === validCurrentPage

        return (
          <Button
            key={pageNumber}
            variant={isActive ? "primary" : "ghost"}
            size="md"
            onClick={() => handlePageClick(pageNumber)}
            aria-current={isActive ? "page" : undefined}
            className="w-8 h-8 p-2 rounded-100 text-14 font-500 min-w-8"
          >
            {pageNumber}
          </Button>
        )
      })
    }

    return (
      <PaginationBase className={className}>
        {/* Mobile View: Compact navigation with page indicator*/}
        <div className="flex items-center gap-6 tab:hidden">
          {!isFirstPage && (
            <IconButton
              variant="primary"
              size="md"
              icon="ArrowLeft"
              onClick={handlePrevious}
              aria-label="Go to previous page"
            />
          )}
          <span className="text-14 font-500 text-root-text-color-base leading-20">
            {validCurrentPage} of {totalPages}
          </span>
          {!isLastPage && (
            <IconButton
              variant="primary"
              size="md"
              icon="ArrowRight"
              onClick={handleNext}
              aria-label="Go to next page"
            />
          )}
        </div>

        {/* Desktop View: Full pagination with page numbers */}
        <PaginationContent className="hidden tab:flex items-center gap-6">
          {!isFirstPage && (
            <PaginationItem>
              <Button
                variant="primary"
                size="md"
                onClick={handlePrevious}
                aria-label="Go to previous page"
                iconStart="ArrowLeft"
                className="gap-1 pl-4 pr-4 py-2 rounded-100"
              >
                Previous
              </Button>
            </PaginationItem>
          )}
          <PaginationItem>
            <div className="flex items-center gap-2">{renderPageNumbers()}</div>
          </PaginationItem>
          {!isLastPage && (
            <PaginationItem>
              <Button
                variant="primary"
                size="md"
                onClick={handleNext}
                aria-label="Go to next page"
                className="gap-1 pl-4 pr-4 py-2 rounded-100"
              >
                <span>Next</span>
                <Icon name="ArrowRight" />
              </Button>
            </PaginationItem>
          )}
        </PaginationContent>
      </PaginationBase>
    )
  }
)

Pagination.displayName = "Pagination"
